import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { getRetryDelaySeconds } from "@ease/shared";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";
import { apiFetch } from "@/lib/api-client";

export interface AnalyticsRealtimeSnapshot {
  generatedAt: string;
  windowSeconds: number;
  activeVisitors: number;
  currentPageViews: number;
  topPages: Array<{
    path: string;
    title: string | null;
    pageViews: number;
  }>;
}

interface RealtimeEnvelope {
  type?: string;
  payload?: Record<string, unknown>;
}

const POLL_INTERVAL_MS = 10_000;
const RECONNECT_MAX_ATTEMPTS = 10;

export function useAnalyticsRealtime() {
  const { isLoading: isAuthLoading } = useAuth();
  const [snapshot, setSnapshot] = useState<AnalyticsRealtimeSnapshot | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);

  const refreshSnapshot = useCallback(async () => {
    try {
      const response = await apiFetch<{ data: AnalyticsRealtimeSnapshot }>("/v1/analytics/realtime");
      setSnapshot(response.data);
    } catch {
      // Keep the last known snapshot. The dashboard already renders historical analytics.
    }
  }, []);

  const connect = useCallback(async () => {
    if (isAuthLoading) {
      return;
    }

    const bearerToken = await requestBearerToken();
    if (!bearerToken) {
      setIsConnected(false);
      return;
    }

    try {
      const ws = new WebSocket(buildRealtimeWebsocketUrl(resolveRealtimeBaseUrl(), bearerToken));
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        reconnectAttempts.current = 0;
        setIsConnected(true);
      });

      ws.addEventListener("message", (event) => {
        const parsed = parseEnvelope(event.data);
        if (parsed?.type !== "analytics.realtime.count" || !parsed.payload) {
          return;
        }

        const nextSnapshot = toRealtimeSnapshot(parsed.payload);
        if (nextSnapshot) {
          setSnapshot(nextSnapshot);
        }
      });

      ws.addEventListener("close", () => {
        setIsConnected(false);
        wsRef.current = null;
        scheduleReconnect(connect, reconnectAttempts, reconnectTimerRef);
      });

      ws.addEventListener("error", () => {
        setIsConnected(false);
      });
    } catch {
      setIsConnected(false);
      scheduleReconnect(connect, reconnectAttempts, reconnectTimerRef);
    }
  }, [isAuthLoading]);

  useEffect(() => {
    void refreshSnapshot();
    pollTimerRef.current = setInterval(() => {
      void refreshSnapshot();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [refreshSnapshot]);

  useEffect(() => {
    void connect();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  return {
    snapshot,
    isConnected,
    refreshSnapshot,
  };
}

async function requestBearerToken(): Promise<string | null> {
  try {
    const sessionResult = await authClient.getSession();
    if (!sessionResult?.data?.session?.token) {
      return null;
    }
    return sessionResult.data.session.token;
  } catch {
    return null;
  }
}

function scheduleReconnect(
  reconnect: () => Promise<void>,
  reconnectAttempts: MutableRefObject<number>,
  reconnectTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
) {
  if (reconnectAttempts.current >= RECONNECT_MAX_ATTEMPTS) {
    return;
  }

  const delayMs = getRetryDelaySeconds(reconnectAttempts.current + 1, { baseMs: 1_000 }) * 1_000;
  reconnectAttempts.current += 1;
  reconnectTimerRef.current = setTimeout(() => {
    void reconnect();
  }, delayMs);
}

function resolveRealtimeBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  const configured = env.VITE_REALTIME_URL?.trim();
  if (configured) {
    return configured;
  }

  return window.location.origin;
}

function buildRealtimeWebsocketUrl(
  baseUrl: string,
  token: string,
): string {
  const url = new URL(baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("token", token);
  url.searchParams.set("channels", "analytics.*");
  return url.toString();
}

function extractBearerToken(result: unknown): string | null {
  const record = result && typeof result === "object" ? result as Record<string, unknown> : null;
  const data = record?.data && typeof record.data === "object" ? record.data as Record<string, unknown> : null;
  const candidates = [
    data?.token,
    record?.token,
    data?.accessToken,
    record?.accessToken,
  ];

  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? null;
}

function parseEnvelope(raw: unknown): RealtimeEnvelope | null {
  try {
    return JSON.parse(typeof raw === "string" ? raw : String(raw)) as RealtimeEnvelope;
  } catch {
    return null;
  }
}

function toRealtimeSnapshot(payload: Record<string, unknown>): AnalyticsRealtimeSnapshot | null {
  if (
    typeof payload.generatedAt !== "string" ||
    typeof payload.windowSeconds !== "number" ||
    typeof payload.activeVisitors !== "number" ||
    typeof payload.currentPageViews !== "number" ||
    !Array.isArray(payload.topPages)
  ) {
    return null;
  }

  return {
    generatedAt: payload.generatedAt,
    windowSeconds: payload.windowSeconds,
    activeVisitors: payload.activeVisitors,
    currentPageViews: payload.currentPageViews,
    topPages: payload.topPages
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const record = row as Record<string, unknown>;
        if (typeof record.path !== "string" || typeof record.pageViews !== "number") {
          return null;
        }
        return {
          path: record.path,
          title: typeof record.title === "string" ? record.title : null,
          pageViews: record.pageViews,
        };
      })
      .filter((row): row is AnalyticsRealtimeSnapshot["topPages"][number] => row !== null),
  };
}
